import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card, Space, Tag, Typography, Avatar, Divider, message, Spin, Rate, Popconfirm } from 'antd';
import { 
  ArrowLeftOutlined, 
  UserOutlined, 
  CalendarOutlined,
  TagOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import {
  setCurrentView,
  updateSnippetInResults,
  setSelectedSnippet,
  selectSelectedSnippet,
} from '@src/app/reducers/snippets/snippetsSlice';
import { RootState } from '@src/app/store';
import { showCreateSnippetModal } from '@src/features/interface/interfaceSlice';
import useTranslation from '@hooks/useTranslation';
import { rateSnippet, deleteSnippet } from '@src/api/snippet/snippetApi';
import MarkdownViewer from '@components/MarkdownViewer/MarkdownViewer';
import styles from './SnippetDetail.module.css';
import SvgFromString from '@components/SvgFromString/SvgFromString';

const { Title, Text, Paragraph } = Typography;

/**
 * Component for displaying detailed view of a snippet
 * @returns React.JSX.Element
 */
const SnippetDetail = (): React.JSX.Element => {
  const dispatch = useDispatch();
  const t = useTranslation('Snippets.Detail');
  const cw = t.withBaseKey('CommonWords');
  
  const selectedSnippet = useSelector(selectSelectedSnippet);
  const { searchConfig } = useSelector((state: RootState) => state.snippets);
  const [rating, setRating] = useState(3.5);
  const [isRating, setIsRating] = useState(false);

  /**
   * Handles going back to the list view
   */
  const handleBack = useCallback(() => {
    dispatch(setCurrentView('list'));
  }, [dispatch]);

  /**
   * Handles rating the snippet
   */
  const handleRating = useCallback(async (newRating: number) => {
    if (!selectedSnippet || isRating) { return; }
    
    setIsRating(true);
    try {
      const response = await rateSnippet(selectedSnippet.id, {
        snippetId: selectedSnippet.id,
        rating: newRating,
      });
      
      setRating(newRating);
      
      const updatedSnippet = {
        ...selectedSnippet,
        rating: {
          average: newRating,
          total: response.total,
        },
      };
      
      dispatch(setSelectedSnippet(updatedSnippet));
      dispatch(updateSnippetInResults({
        snippetId: selectedSnippet.id,
        updates: {
          rating: updatedSnippet.rating,
        },
      }));
      
      message.success(t('RatingSuccess', 'Rating updated successfully!'));
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || t('RatingError', 'Failed to update rating');
      message.error(errorMessage);
    } finally {
      setIsRating(false);
    }
  }, [dispatch, selectedSnippet, isRating, t]);

  /**
   * Handles editing the snippet
   */
  const handleEdit = useCallback(() => {
    if (!selectedSnippet) { return; }
    
    dispatch(showCreateSnippetModal({
      snippetId: selectedSnippet.id,
      snippetCode: '', // Will be loaded by the modal
    }));
  }, [dispatch, selectedSnippet]);

  /**
   * Handles deleting the snippet
   */
  const handleDelete = useCallback(async () => {
    if (!selectedSnippet) { return; }
    
    try {
      await deleteSnippet(selectedSnippet.id);
      message.success(t('DeleteSuccess', 'Snippet deleted successfully!'));
      
      // Go back to list view after successful deletion
      dispatch(setCurrentView('list'));
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || t('DeleteError', 'Failed to delete snippet');
      message.error(errorMessage);
    }
  }, [dispatch, selectedSnippet, t]);

  /**
   * Formats a date string for display
   */
  const formatDate = useCallback((dateString: string) => {
    // Format a date string using the user's locale, falling back to 'en-US' if not available
    const userLocale = navigator.language || 'en-US';
    return new Date(dateString).toLocaleDateString(userLocale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }, []);

  if (!selectedSnippet) {
    return (
      <div className={styles.Container}>
        <div className={styles.LoadingContainer}>
          <Spin size="large" />
          <Text type="secondary" className={styles.LoadingText}>
            {cw('Loading')}
          </Text>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.Container}>
      {/* Header with back button */}
      <div className={styles.Header}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={handleBack}
          className={styles.BackButton}
        >
          {t('Back')}
        </Button>
        <Title level={4} className={styles.HeaderTitle}>
          {t('Title')}
        </Title>
      </div>

      <div className={styles.Content}>
        {/* Snippet header */}
        <div className={styles.SnippetHeader}>
          <div className={styles.IconContainer}>
            {selectedSnippet.iconSvg ? (
              <SvgFromString
                svgStr={selectedSnippet.iconSvg} 
                width={48} 
                height={48}
              />
            ) : (
              <div className={styles.DefaultIcon}>
                <Text strong className={styles.DefaultIconText}>
                  {selectedSnippet.title.charAt(0).toUpperCase()}
                </Text>
              </div>
            )}
          </div>

          <div className={styles.TitleSection}>
            <div className={styles.TitleRow}>
              <Title level={2} className={styles.Title}>
                {selectedSnippet.title}
              </Title>
              <Tag color={selectedSnippet.category.color} className={styles.CategoryTag}>
                {selectedSnippet.category.name}
              </Tag>
            </div>
            
            <div className={styles.MetaInfo}>
              <Space size="small" className={styles.AuthorInfo}>
                <Avatar size="small" icon={<UserOutlined />} />
                <Text type="secondary" className={styles.Author}>
                  <Text strong>{selectedSnippet.author.name}</Text>
                </Text>
              </Space>

              <div className={styles.VerticalSeparator} />

              <Space size="small">
                <CalendarOutlined />
                <Text type="secondary">
                  {formatDate(selectedSnippet.updatedAt || selectedSnippet.createdAt)}
                </Text>
              </Space>

              <div className={styles.VerticalSeparator} />

              <div className={styles.RatingSection}>
                <Rate
                  value={rating}
                  onChange={handleRating}
                  disabled={isRating || searchConfig.visibility === 'private'}
                  allowHalf
                  className={styles.RatingComponent}
                />
                <Text type="secondary" className={styles.RatingCount}>
                  {selectedSnippet.rating?.total ? `(${selectedSnippet.rating.total})` : '(0)'}
                </Text>
              </div>
            </div>

            {/* Actions Section - Only show for private snippets */}
            {searchConfig.visibility === 'private' && (
              <div className={styles.ActionsSection}>
                <Space size="middle">
                  <Button
                    type="default"
                    icon={<EditOutlined />}
                    onClick={handleEdit}
                    className={styles.EditButton}
                  >
                    {t('Edit', 'Edit')}
                  </Button>
                  
                  <Popconfirm
                    title={t('DeleteConfirmTitle', 'Delete Snippet')}
                    description={t('DeleteConfirmDescription', 'This action cannot be undone. Are you sure you want to delete this snippet?')}
                    onConfirm={handleDelete}
                    okText={t('Delete', 'Delete')}
                    cancelText={t('Cancel', 'Cancel')}
                    okType="danger"
                  >
                    <Button
                      type="default"
                      danger
                      icon={<DeleteOutlined />}
                      className={styles.DeleteButton}
                    >
                      {t('Delete', 'Delete')}
                    </Button>
                  </Popconfirm>
                </Space>
              </div>
            )}
             
            <Paragraph className={styles.Description}>
              {selectedSnippet.shortDescription}
            </Paragraph>

          </div>
        </div>

        <Divider />

        {/* Tags */}
        {selectedSnippet.tags && selectedSnippet.tags.length > 0 && (
          <div className={styles.TagsSection}>
            <Space align="center" size="small" className={styles.TagsHeader}>
              <TagOutlined />
              <Text strong>{t('Tags')}</Text>
            </Space>
            <div className={styles.TagsContainer}>
              <Space wrap size="small">
                {selectedSnippet.tags.map((tag) => (
                  <Tag key={tag} className={styles.Tag}>
                    {tag}
                  </Tag>
                ))}
              </Space>
            </div>
          </div>
        )}

        <Divider />

        {/* Content */}
        <div className={styles.ContentSection}>
          <Space align="center" size="small" className={styles.ContentHeader}>
            <EyeOutlined />
            <Text strong>{t('Details')}</Text>
          </Space>
          
          <div className={styles.MarkdownContainer}>
            {selectedSnippet.descriptionMarkdown ? (
              <MarkdownViewer
                markdown={selectedSnippet.descriptionMarkdown}
                docsUrl=""
                disableSnippets
                disablePermalink
              />
            ) : (
              <div className={styles.NoContent}>
                <Text type="secondary" italic>
                  {t('NoContent')}
                </Text>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SnippetDetail;
